<template>
  <v-container fluid class="px-8 py-4">
    <v-row>
      <v-col cols="8">
        <breadcrumbs></breadcrumbs>
        <h1>Organizations</h1>
      </v-col>
      <v-col cols="4">
        <div class="text-right mt-4">
          <v-btn
            :to="{ name: 'ochinOrganizationFormCreate' }"
            color="primary"
            data-cy="och-orgList-create"
            outlined
          >
            Create Organization
          </v-btn>
        </div>
      </v-col>
    </v-row>
    <v-divider class="mb-8"></v-divider>

    <v-card outlined>
      <v-card-text>
        <v-data-table
          :headers="headers"
          :items="list"
          :loading="loading"
          :search="search"
          class="och-orgList"
          data-cy="och-orgList"
          fixed-header
        >
          <template v-slot:top>
            <v-row>
              <v-col cols="12" sm="12" md="6" lg="3">
                <v-text-field
                  class="mb-4"
                  clearable
                  dense
                  hide-details
                  outlined
                  placeholder="Search organization..."
                  v-model="search"
                >
                  <template v-slot:prepend-inner>
                    <v-icon class="mr-2 mt-1" color="grey" small
                      >fas fa-search</v-icon
                    >
                  </template>
                </v-text-field></v-col
              >
            </v-row>
          </template>
          <template v-slot:item="{ item }">
            <tr class="och-orgList__tableRow" data-cy="och-orgList__tableRow">
              <td>
                <router-link
                  :to="{
                    name: 'ochinOrganizationFormEdit',
                    params: { organizationId: item.organizationId },
                  }"
                  >{{ item.name }}</router-link
                >
              </td>
              <td>{{ item.domain }}</td>
              <td>{{ getLabelShortByStatus(item.status) }}</td>
              <td>{{ item.careSetting }}</td>
              <td>{{ getYN(item.contractSigned) }}</td>
              <td>{{ getGoLiveDate(item.salesforceId) }}</td>
              <td>
                <service-area-dialog
                  data-cy="service-area"
                  :class="{ 'och-disable-events': item.serviceAreaId }"
                  :organization="item"
                  :serviceAreaId="item.serviceAreaId"
                ></service-area-dialog>
              </td>
              <td>
                {{ item.patientPopulationAverageYearlyVistsPerPatient || "--" }}
              </td>
              <td>{{ getUser(item.businessDevelopmentLead) || "--" }}</td>
              <td>{{ getUser(item.ochinProjectManager) || "--" }}</td>
              <td class="och-orgList__commentColumn">
                {{ item.comments || "--" }}
              </td>
              <td>
                <v-menu offset-y>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      :data-cy="`item${item.organizationId}-menu`"
                      color="primary"
                      outlined
                      small
                      v-bind="attrs"
                      v-on="on"
                    >
                      Menu <v-icon small class="ml-1">fas fa-caret-down</v-icon>
                    </v-btn>
                  </template>
                  <v-list>
                    <v-list-item-group>
                      <v-list-item
                        :to="{
                          name: 'ochinOrganizationFormEdit',
                          params: { organizationId: item.organizationId },
                        }"
                      >
                        <v-list-item-icon>
                          <v-icon small class="mr-2" color="accent"
                            >fas fa-edit fa-fw</v-icon
                          >
                        </v-list-item-icon>
                        <v-list-item-content
                          ><v-list-item-title data-cy="edit-btn"
                            >Edit</v-list-item-title
                          ></v-list-item-content
                        >
                      </v-list-item>
                    </v-list-item-group>
                    <v-list-item-group>
                      <v-list-item @click="navigate(item)"
                        ><v-list-item-icon>
                          <v-icon small class="mr-2" color="accent"
                            >fas fa-link fa-fw</v-icon
                          >
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title
                            >Shareable Link</v-list-item-title
                          ></v-list-item-content
                        >
                      </v-list-item>
                    </v-list-item-group>
                  </v-list>
                </v-menu>
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import Breadcrumbs from "@/views/Breadcrumbs";
import ServiceAreaDialog from "./ServiceAreaDialog";

export default {
  components: {
    Breadcrumbs,
    ServiceAreaDialog,
  },
  computed: {
    ...mapGetters({
      getByUsername: "coreTeam/getByUsername",
      getSalesforceAccount: "salesforce/getById",
      getLabelShortByStatus: "organizationStatus/getLabelShortByStatus",
    }),
    ...mapState({
      list: state => state.organization.listAdmin,
      ochinUserList: state => state.coreTeam.ochinUserList,
      salesforceList: state => state.salesforce.list,
    }),
  },
  async created() {
    if (this.list > 1) {
      return;
    }
    this.loading = true;
    await Promise.all([
      this.fetchOrganizationListAdmin(),
      this.fetchOchinUserList(),
      this.listSalesforceAccounts(),
    ]);
    this.loading = false;
  },
  data() {
    return {
      loading: false,
      headers: [
        { text: "Name", value: "name", align: "start" },
        { text: "Domain", value: "domain" },
        { text: "Status", value: "status" },
        { text: "Care Setting", value: "careSetting" },
        { text: "Contract Signed", value: "contractSigned" },
        { text: "Go-Live Date", value: "goLiveDate" },
        { text: "Service Area", value: "serviceAreaId" },
        {
          text: "Annual # Visits",
          value: "patientPopulationAverageYearlyVistsPerPatient",
        },
        { text: "Biz Dev Lead", value: "businessDevelopmentLead" },
        { text: "Project Manager", value: "ochinProjectManager" },
        {
          text: "BD-PM Handoff Comments",
          value: "comments",
        },
        { text: "Actions", value: "action" },
      ],
      search: null,
    };
  },
  methods: {
    getGoLiveDate(salesforceId) {
      if (salesforceId && this.salesforceList) {
        const salesforceAccount = this.getSalesforceAccount(salesforceId);
        if (salesforceAccount && salesforceAccount.goLiveDate) {
          return salesforceAccount.goLiveDate;
        }
      }
      return "--";
    },
    getUser(username) {
      if (username) {
        const user = this.getByUsername(username);
        return user ? `${user.firstName} ${user.lastName}` : "";
      }
    },
    getYN(value) {
      return value === 1 ? "Yes" : "No";
    },
    navigate(item) {
      window.open(`/member/account/${item.organizationGuid}/create-account`);
    },
    userFullName(item) {
      return `${item.firstName} ${item.lastName} (${item.email})`;
    },
    ...mapActions({
      create: "organization/create",
      fetchOchinUserList: "coreTeam/ochinUserList",
      fetchOrganizationListAdmin: "organization/listAdmin",
      listSalesforceAccounts: "salesforce/list",
      update: "organization/update",
    }),
  },
  notifications: {
    notifyError: {
      title: "Error",
      type: "warn",
    },
    notifyWarn: {
      title: "Warning",
      type: "warn",
    },
  },
};
</script>
<style scoped>
.och-orgList__commentColumn {
  max-width: 200px;
}

.och-orgList th {
  padding-bottom: 10px !important;
  padding-top: 10px !important;
  vertical-align: top;
}

.och-orgList__tableRow td {
  padding-bottom: 10px !important;
  padding-top: 10px !important;
  vertical-align: top;
}

.och-disable-events {
  pointer-events: none;
}
</style>
